import { type GetPromptsOutput, type ReportTypeEnum } from "@api/rest/types-and-hooks";
import { TemplateStatus } from "@pages/Templates/constants";

export interface PromptReportTypeMismatch {
  fieldId: number;
  promptId: number;
  sectionTitle: string;
  fieldTitle: string;
  promptName: string;
}

interface SectionWithPrompts {
  title: string;
  section_fields: readonly { id: number; title: string; prompt: { id: number } | null }[];
}

/** A prompt is valid for a report type when it's tagged for it. Untagged
 * prompts (no report types) are valid everywhere, and with no report type
 * there's nothing to check against. */
export function isPromptValidForReportType(
  prompt: Pick<GetPromptsOutput, "report_types">,
  reportType: string,
): boolean {
  return !reportType || prompt.report_types.length === 0 || prompt.report_types.includes(reportType as ReportTypeEnum);
}

/** Stable identity for a mismatch list: the same fields with the same
 * prompts. Used for list keys and to tie an acknowledgment to its list. */
export function promptMismatchKey(mismatch: PromptReportTypeMismatch): string {
  return `${mismatch.fieldId}:${mismatch.promptId}`;
}

/** Fields whose AI prompt is tagged only for other report types. Prompts
 * missing from `prompts` are skipped, since their tags are unknown. */
export function findPromptReportTypeMismatches(
  sections: readonly SectionWithPrompts[],
  prompts: readonly GetPromptsOutput[],
  reportType: string,
): PromptReportTypeMismatch[] {
  const promptsById = new Map(prompts.map((p) => [p.id, p]));
  return sections.flatMap((section) =>
    section.section_fields.flatMap((field) => {
      const prompt = field.prompt ? promptsById.get(field.prompt.id) : undefined;
      if (!prompt || isPromptValidForReportType(prompt, reportType)) return [];
      return [
        {
          fieldId: field.id,
          promptId: prompt.id,
          sectionTitle: section.title,
          fieldTitle: field.title,
          promptName: prompt.name,
        },
      ];
    }),
  );
}

/** Whether a Details save gets the prompt check: only one that switches
 * the report type or publishes. */
export function detailsSaveChecksPrompts(
  template: { report_type: string; status: string },
  edits: { reportType: string; status: string },
): boolean {
  const switchesType = edits.reportType !== template.report_type;
  const publishes = edits.status === TemplateStatus.PUBLISHED && template.status !== TemplateStatus.PUBLISHED;
  return switchesType || publishes;
}

/** Mismatches to confirm before a Details save, checked against the type
 * the template is about to have. Empty when the save isn't checked. */
export function promptMismatchesForDetailsSave(
  template: { report_type: string; status: string; sections: readonly SectionWithPrompts[] },
  edits: { reportType: string; status: string },
  prompts: readonly GetPromptsOutput[],
): PromptReportTypeMismatch[] {
  if (!detailsSaveChecksPrompts(template, edits)) return [];
  return findPromptReportTypeMismatches(template.sections, prompts, edits.reportType);
}
